import { ProductCardModel } from 'src/business/services/client.generated';
import { SourceSet, UmbracoIcon, UmbracoImage, UmbracoSourceset } from 'src/core-lib/umbraco/umbraco-image.model';
import { PageUtils } from 'src/utils/storage.util';

export namespace ImageUtil {
    const QUERY_ENTRIES = [
        ['bgcolor', 'transparent'],
        ['rsampler', 'mitchell'],
        ['format', 'webp'],
        ['rmode', 'pad'],
    ];
    const MAX_IMG_SIZE = 1920;
    const MIN_IMG_SIZE = 320;
    const SIZES = [320, 375, 425, 768, 1024, 1440, 1920];
    export const DENSITIES = [1, 3 / 2, 2, 3, 4];

    // export function deviceRatio(srcset:SourceSet[]): SourceSet[] {
    //     return srcset.reduce((acc,cur) => acc.concat(),[]);

    //     `(-Webkit-min-device-pixel-ratio: ${Function('return')}) `,
    //     `(-moz-min-device-pixel-ratio: ${Function('return')})`,
    //     `(-o-min-device-pixel-ratio: ${})`,
    //     `(min-device-pixel-ratio: ${Function('return')})`,
    // }

    export function aspectRatio(ratio?: string): [string, string, string] {
        if (!ratio || ratio == '')
            return [MAX_IMG_SIZE.toString(), MAX_IMG_SIZE.toString(), '1/1'];
        var [width, height] = ratio.split(':');
        if (!height) {
            if (+width > MAX_IMG_SIZE)
                width = MAX_IMG_SIZE.toString()
            return [width, width, '1/1'];
        }
        var aspect = `${width}/${height}`;
        if (+width > MAX_IMG_SIZE) {
            height = (+height / +width * MAX_IMG_SIZE).toString();
            width = MAX_IMG_SIZE.toString();
        }

        return [width, height, aspect]
    }
    export function style(auto?: 'height' | 'width' | 'none' | null | undefined): string {
        if (auto === 'none')
            return '';
        auto = auto ?? 'height';
        let width = 'auto';
        let height = 'auto';
        if (auto === 'height')
            width = '100%';
        else
            height = '100%';
        return `width:${width}; height:${height};`;
    }
    export function isUmbracoImage(image: string | UmbracoImage | UmbracoIcon): image is UmbracoImage {
        return !(typeof image === 'string') && image.type === 'image';
    }
    export function isUmbracoIcon(image: string | UmbracoIcon): image is UmbracoIcon {
        return !(typeof image === 'string') && image.type === 'svg';
    }
    export function getURL(img: UmbracoImage | string, query?: { [key: string]: string | number }): URL {
        let _url: URL;
        if (img == null)
            return;

        if (ImageUtil.isUmbracoImage(img)) {
            _url = new URL(img.imageUrl, window.origin);
            var [width, height, aspectRatio] = ImageUtil.aspectRatio(img.aspectRatio);
            _url.searchParams.set('width', width);
            _url.searchParams.set('height', height);
            if ('focalPoint' in img)
                _url.searchParams.set('rxy', `${img.focalPoint.y},${img.focalPoint.x}`);
            QUERY_ENTRIES.forEach(([key, value]) => _url.searchParams.has(key) ? null : _url.searchParams.append(key, value));
            Object.entries(query ?? {}).forEach(([key, value]) => _url.searchParams.set(key, '' + value));
        } else if (ImageUtil.isUmbracoIcon(img)) {
            _url = new URL(img.imageUrl, window.origin);
        } else {
            _url = new URL(img, window.origin);
            QUERY_ENTRIES.forEach(([key, value]) => _url.searchParams.has(key) ? null : _url.searchParams.append(key, value));
            Object.entries(query ?? {}).forEach(([key, value]) => _url.searchParams.set(key, '' + value));
        }
        if (_url.pathname.endsWith('.svg')) {
            _url.search = '';
            return _url;
        }



        return _url;
    }


    export function fallbackSrcset(use: UmbracoImage | string): SourceSet[] {
        var [width, height] = isUmbracoImage(use) ? aspectRatio(use.aspectRatio) : [1, 1];
        var aspect = +height / +width;
        var fn = (_size, _aspect) => {
            return {
                media: `(max-width: ${_size}px) `,
                srcset: ImageUtil.getURL(use, { width: Math.min(_size, MAX_IMG_SIZE), height: Math.min(_size, MAX_IMG_SIZE) * _aspect }),
            }
        }
        return SIZES
            .reduce((acc, _size, i, a) => acc.concat(fn(_size, aspect)), []);
    }

    export function productCardSrcset(use: string, span: number = 1): SourceSet[] {
        if (!use || use == '')
            return [];
        return [150, 300, 465, 600].map(size => ({
            srcset: ImageUtil.getURL(use, { width: size * span, height: size }),
        }));
    }
    export function iconSrcset(use: string): SourceSet[] {
        return [
            {
                media: `(max-width: 768px)`,
                srcset: ImageUtil.getURL(use, { width: 20 }),
            },
            {
                media: `(min-width: 769px)`,
                srcset: ImageUtil.getURL(use, { width: 50 }),
            }
        ];
    }

    export function mapSourceset(sourceset: UmbracoSourceset[], media: UmbracoImage | string) {
        function mapMedia(_media: UmbracoImage | string, size: number) {
            var __media = structuredClone(_media) as UmbracoImage;
            var _url = new URL(__media.imageUrl);
            var _aspectRatio = +_url.searchParams.get('width') / +_url.searchParams.get('height');
            _url.searchParams.set('width', size.toString());
            _url.searchParams.set('height', Math.floor(size * _aspectRatio).toString());
            __media.imageUrl = _url.href;
            return __media;
        }
        var maxW = 0;
        if (sourceset?.length > 0)
            return [
                ...SIZES
                    .reduce((acc, curr, i, arr) => {
                        var listOfSourcesets = [];
                        var _set = sourceset.find((__set, _i, _arr) => __set.maxDisplayWidth >= curr) ?? {
                            maxDisplayWidth: MAX_IMG_SIZE,
                            media,
                        };
                        listOfSourcesets.push({
                            maxDisplayWidth: curr,
                            media: mapMedia(_set.media, curr),
                            imageWidth: curr,
                        });
                        if (i + 1 >= arr.length || arr[i + 1] > _set.maxDisplayWidth)
                            listOfSourcesets.push({
                                maxDisplayWidth: _set?.maxDisplayWidth,
                                media: mapMedia(_set.media, _set?.maxDisplayWidth),
                                imageWidth: _set?.maxDisplayWidth,
                            });


                        return acc.concat(listOfSourcesets);
                    }, [])
                    .filter((value, index, self) => self.findIndex(_value => value.maxDisplayWidth === _value.maxDisplayWidth) === index)
                    .sort((a, b) => a?.maxDisplayWidth - b?.maxDisplayWidth)
                    .map(_set => {
                        maxW = _set?.maxDisplayWidth;
                        return mapSingleSet(_set, media)
                    })
                ,
                {
                    media: ``,
                    srcset: ImageUtil.getURL(media),
                }
            ];
        return [
            ...ImageUtil.fallbackSrcset(media),
            {
                media: ``,
                srcset: ImageUtil.getURL(media),
            }
        ]
    }
    function mapSingleSet(_set: UmbracoSourceset, media: UmbracoImage | string) {
        var use = _set?.media ?? media;
        var [width, height] = isUmbracoImage(use) ? aspectRatio(use.aspectRatio) : [1, 1];
        var aspect = +height / +width;
        return {
            media: `(max-width: ${_set?.maxDisplayWidth}px)`,
            srcset: ImageUtil.getURL(use, { width: _set?.maxDisplayWidth, height: _set?.maxDisplayWidth * aspect }),
        };
    }
}
